/* eslint-disable no-param-reassign */
import { ActionTree, Module } from 'vuex';
import AuthService from '@/services/AuthService';
import UserService from '@/services/UserService';
import UsersService from '@/services/UsersService';
import { Client, User } from '@/types';
import { CustomError, RootState } from '../types';

export interface SendAccountRecoveryEmailDto {
  username: string;
  name: string;
  email: string;
  phone?: string;
  organisation?: string;
  additional?: string;
  recaptchaResponse: string;
  redirectUri?: string;
}

interface AuthState {
  authenticated: boolean;
  user: User | null;
}

const state = (): AuthState => ({
  authenticated: false,
  user: null,
});

const actions: ActionTree<AuthState, RootState> = {
  async login({ commit }, { usernameOrEmail, password }): Promise<boolean> {
    try {
      commit('CLEAR_ERROR', null, { root: true });
      commit('LOGOUT');

      await AuthService.login(usernameOrEmail, password);

      const user = await UserService.getCurrentUser();

      commit('LOGIN', user);

      return true;
    } catch (e) {
      const error = e as CustomError;
      let { message } = error;
      if (error.error === 'CHANGE_SHARED_PASSWORD_UNAUTHORISED') {
        message = 'Cannot change shared passwords';
      }

      commit('SET_ERROR', message, { root: true });
      return false;
    }
  },

  async requestPasswordReset({ commit }, { usernameOrEmail, clientId, recaptchaResponse }) {
    try {
      commit('CLEAR_ERROR', null, { root: true });
      return await AuthService.forgotPassword({
        usernameOrEmail,
        clientId,
        recaptchaResponse,
      });
    } catch (e) {
      const error = e as CustomError;
      commit('SET_ERROR', error.message, { root: true });
      return false;
    }
  },

  async resetPassword(
    { commit, dispatch },
    { username, token, newPassword, clientId },
  ): Promise<boolean> {
    try {
      commit('CLEAR_ERROR', null, { root: true });
      await AuthService.resetPassword({ username, token, newPassword, clientId });
      return dispatch('login', { usernameOrEmail: username, password: newPassword });
    } catch (e) {
      const error = e as CustomError;
      commit('SET_ERROR', error.message, { root: true });
      return false;
    }
  },

  async changePassword(
    { commit },
    { oldPassword, newPassword }: { oldPassword: string; newPassword: string },
  ): Promise<boolean> {
    try {
      commit('CLEAR_ERROR', null, { root: true });
      await UserService.changePassword(oldPassword, newPassword);
      return true;
    } catch (e) {
      const error = e as CustomError;
      commit('SET_ERROR', error.message, { root: true });
      return false;
    }
  },

  async requestAccountRecovery({ commit }, payload: SendAccountRecoveryEmailDto) {
    try {
      commit('CLEAR_ERROR', null, { root: true });
      await UsersService.sendAccountRecoveryEmail(payload);
      return true;
    } catch (e) {
      const error = e as CustomError;
      commit('SET_ERROR', error.message, { root: true });
      return false;
    }
  },
};

const mutations = {
  UPDATE_USER(s: AuthState, user: User | null): void {
    s.user = user;
  },

  LOGOUT(s: AuthState): void {
    s.user = null;
    s.authenticated = false;
  },

  LOGIN(s: AuthState, user: User): void {
    s.authenticated = true;
    s.user = user;
  },
};

const getters = {
  user: (s: AuthState): User | null => s.user,
  clients: (s: AuthState): Client[] => (s.user && s.user.clients) || [],
  isMultipleUser: (s: AuthState): boolean => {
    return s.user?.isMultipleUser ?? false;
  },

  email: (s: AuthState): string | undefined => {
    return s.user?.email;
  },
};

const store: Module<AuthState, RootState> = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};

export default store;
